import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { Building2, CheckCircle2, GraduationCap, Users, LogIn, Wallet } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { OrgDashboardApi, OrgPortalApi } from '@/services/org.api';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Input';
import dayjs from 'dayjs';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function StatCard({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof Building2;
  label: string;
  value: string | number;
  tone: string;
}) {
  return (
    <div className="card flex items-center gap-4 p-5">
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${tone}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
        <div className="mt-0.5 text-2xl font-semibold text-slate-900">{value}</div>
      </div>
    </div>
  );
}

export function OrgOverviewPage() {
  const navigate = useNavigate();
  const { data: org } = useQuery({ queryKey: ['org-me'], queryFn: OrgPortalApi.me });
  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['org-dashboard-overview'],
    queryFn: OrgDashboardApi.overview,
  });

  const [period, setPeriod] = useState<{ month: number; year: number } | null>(null);
  const requestedPeriod = period ?? overview?.defaultPeriod ?? null;

  const { data: trend, isFetching: trendLoading } = useQuery({
    queryKey: ['org-dashboard-attendance-trend', requestedPeriod?.month, requestedPeriod?.year],
    queryFn: () => OrgDashboardApi.attendanceTrend(requestedPeriod?.month, requestedPeriod?.year),
    enabled: !!overview, // wait for overview so we don't fire with an unset period first
  });

  // Once the backend resolves a period (its own "most recent populated
  // month" fallback when the org has no data yet), reflect it in the
  // pickers too — otherwise they'd show an unmatched blank value.
  const effectivePeriod = requestedPeriod ?? (trend ? { month: trend.month, year: trend.year } : null);

  const years = Array.from({ length: 6 }, (_, i) => (effectivePeriod?.year ?? dayjs().year()) - 3 + i);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">{org?.name ?? 'Organization'} — Overview</h1>
          <p className="mt-0.5 text-sm text-slate-500">A snapshot of your organization's schools.</p>
        </div>
        <button
          className="btn-secondary inline-flex shrink-0 items-center gap-1.5 text-sm"
          onClick={() => navigate('/org/select-school')}
        >
          <LogIn className="h-4 w-4" /> Enter a school
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={Building2}
          label="Total Schools"
          value={overviewLoading ? '—' : overview?.schools.total ?? 0}
          tone="bg-indigo-50 text-indigo-600"
        />
        <StatCard
          icon={CheckCircle2}
          label="Active Schools"
          value={overviewLoading ? '—' : overview?.schools.active ?? 0}
          tone="bg-emerald-50 text-emerald-600"
        />
        <StatCard
          icon={GraduationCap}
          label="Total Students"
          value={overviewLoading ? '—' : (overview?.students.total ?? 0).toLocaleString()}
          tone="bg-sky-50 text-sky-600"
        />
        <StatCard
          icon={Users}
          label="Total Teachers"
          value={overviewLoading ? '—' : (overview?.teachers.total ?? 0).toLocaleString()}
          tone="bg-amber-50 text-amber-600"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Students per class */}
        <div className="card p-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-900">Students Per Class</h2>
            <Link to="/org/mis-reports" className="text-xs font-medium text-indigo-600 hover:underline">
              View Report
            </Link>
          </div>
          {overviewLoading ? (
            <div className="py-8 text-center text-sm text-slate-400">Loading…</div>
          ) : !overview?.studentsPerClass.length ? (
            <div className="py-8 text-center text-sm text-slate-400">No class enrollment data yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-slate-500">
                <tr>
                  <th className="py-1.5">Class</th>
                  <th className="py-1.5 text-right">Students</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {overview.studentsPerClass.map((row) => (
                  <tr key={row.className}>
                    <td className="py-2 font-medium text-slate-800">{row.className}</td>
                    <td className="py-2 text-right text-slate-600">{row.count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Least attendance marked schools */}
        <div className="card p-5">
          <h2 className="mb-3 text-sm font-semibold text-slate-900">Least Attendance Marked Schools (Last Day)</h2>
          {overviewLoading ? (
            <div className="py-8 text-center text-sm text-slate-400">Loading…</div>
          ) : !overview?.leastAttendanceMarkedSchools.length ? (
            <div className="py-8 text-center text-sm text-slate-400">No attendance data yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-slate-500">
                <tr>
                  <th className="py-1.5">School</th>
                  <th className="py-1.5 text-right">Marked %</th>
                  <th className="py-1.5 text-right">Present/Total</th>
                  <th className="py-1.5 text-right">Last Marked</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {overview.leastAttendanceMarkedSchools.map((row) => (
                  <tr key={row.schoolId}>
                    <td className="py-2 font-medium text-slate-800">{row.schoolName}</td>
                    <td className="py-2 text-right">
                      <Badge tone={row.markedPct < 50 ? 'red' : row.markedPct < 90 ? 'amber' : 'green'}>
                        {row.markedPct}%
                      </Badge>
                    </td>
                    <td className="py-2 text-right text-slate-600">
                      {row.presentCount}/{row.markedCount}
                    </td>
                    <td className="py-2 text-right text-slate-500">
                      {row.lastMarkedDate ? dayjs(row.lastMarkedDate).format('DD MMM YYYY') : 'Never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Month/year filter for the two trend charts */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-500">Attendance trend for</span>
        <Select
          className="!w-40"
          value={effectivePeriod?.month ?? ''}
          onChange={(e) =>
            setPeriod({ month: Number(e.target.value), year: effectivePeriod?.year ?? dayjs().year() })
          }
        >
          {MONTH_NAMES.map((name, i) => (
            <option key={name} value={i + 1}>
              {name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-28"
          value={effectivePeriod?.year ?? ''}
          onChange={(e) =>
            setPeriod({ month: effectivePeriod?.month ?? dayjs().month() + 1, year: Number(e.target.value) })
          }
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Select>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <TrendChart title="Teachers Present (%)" loading={trendLoading} data={trend?.teachers ?? []} color="#f59e0b" />
        <TrendChart title="Students Present (%)" loading={trendLoading} data={trend?.students ?? []} color="#0ea5e9" />
      </div>

      {/* Weekly fee collection — fees not yet migrated */}
      <div className="card p-5">
        <h2 className="mb-3 text-sm font-semibold text-slate-900">Weekly Fee Collection (Last 10 Weeks)</h2>
        {overview?.fees.available === false ? (
          <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
            <Wallet className="h-8 w-8 text-slate-300" />
            <p className="text-sm text-slate-500">Fees not yet available.</p>
            <p className="text-xs text-slate-400">Fee collection hasn't been migrated for this organization yet.</p>
          </div>
        ) : (
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[]}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="amount" fill="#6366f1" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

function TrendChart({
  title,
  loading,
  data,
  color,
}: {
  title: string;
  loading: boolean;
  data: { date: string; presentPct: number }[];
  color: string;
}) {
  return (
    <div className="card p-5">
      <h2 className="mb-3 text-sm font-semibold text-slate-900">{title}</h2>
      {loading ? (
        <div className="flex h-[220px] items-center justify-center text-sm text-slate-400">Loading…</div>
      ) : data.length === 0 ? (
        <div className="flex h-[220px] items-center justify-center text-sm text-slate-400">
          No attendance data for this period.
        </div>
      ) : (
        <div style={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.map((d) => ({ ...d, day: dayjs(d.date).format('D') }))}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: number) => [`${v}%`, 'Present']} labelFormatter={(d) => `Day ${d}`} />
              <Line type="monotone" dataKey="presentPct" stroke={color} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
